import React, { useEffect } from 'react';
import clsx from 'clsx';
import InputField from '../../Forms/Input/InputField';
import TextAreaField from '../../Forms/Input/TextAreaField';
import SelectField from '../../Forms/Input/SelectField';
import SimpleSearchSelect from '../../Forms/Input/SimpleSearchSelect';
import { useCustomer } from '../../../hooks/useCustomer';
import { ICustomerForm } from '../../../types/customer/customerInterfaces';
import QuickAddEconomicActivity from '../../QuickAdd/EconomicActivity/QuickAddEconomicActivity';

interface CustomerNaturalFormProps {
  form: ICustomerForm;
  updateField: (field: keyof ICustomerForm, value: any) => void;

  taxpayerCategoryDropdown: { id: number; name: string }[];
  economicActivitiesDropdown: { id: number; name: string }[];
  countryDropdown: { id: number; name: string }[];
  departments: { id: number; name: string; code: string }[];
  municipalities: { id: number; name: string }[];
  documentIdentificationTypeDropdown: {
    id: number;
    name: string;
    code: string;
  }[]; // 👈 agrega esto

  onFetchMunicipalities: (departmentCode: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;

  onEconomicActivityCreated: (data: { name: string }) => Promise<void> | void;
  onReloadEconomicActivities: () => void;

  isSubmitting?: boolean;
}

const CustomerNaturalForm: React.FC<CustomerNaturalFormProps> = ({
  form,
  updateField,
  taxpayerCategoryDropdown,
  economicActivitiesDropdown,
  countryDropdown,
  departments,
  municipalities,
  documentIdentificationTypeDropdown,
  onFetchMunicipalities,
  onSubmit,
  onCancel,
  onEconomicActivityCreated,
  onReloadEconomicActivities,
  isSubmitting = false,
}) => {
  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.target.value);
    updateField('departmentId', selectedId);
    updateField('municipalityId', 0);
    const dept = departments.find((d) => d.id === selectedId);
    if (dept) onFetchMunicipalities(dept.code);
  };

  useEffect(() => {
    if (form.departmentId && form.departmentCode) {
      onFetchMunicipalities(form.departmentCode);
    }
  }, [form.departmentId, form.departmentCode]);

  const { errors, clearIdentification } = useCustomer();

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {/* 🔹 Tipo de documento */}
      <SelectField
        label="Tipo de Documento"
        name="documentIdentificationTypeId"
        iconKey="list"
        value={form.documentIdentificationTypeId ?? ''}
        required
        onChange={(e) => {
          const selectedId = Number(e.target.value);
          updateField('documentIdentificationTypeId', selectedId);

          // 👉 Busca el código asociado al ID seleccionado
          const selected = documentIdentificationTypeDropdown.find(
            (t) => t.id === selectedId
          );
          updateField('documentIdentificationTypeCode', selected?.code || '');

          // ✅ Limpiar todos los campos de identificación anteriores
          clearIdentification();
        }}
        options={documentIdentificationTypeDropdown.map((item) => ({
          value: item.id,
          label: item.name,
        }))}
        error={errors?.documentIdentificationTypeId}
      />

      {(() => {
        const selected = documentIdentificationTypeDropdown.find(
          (t) => t.id === form.documentIdentificationTypeId
        );
        const selectedCode = selected?.code;

        return (
          <>
            {/* Nombre siempre visible */}
            <InputField
              label="Nombre"
              name="name"
              value={form.name}
              required
              onChange={(e) => updateField('name', e.target.value)}
              type="text"
              placeholder="Ingrese el nombre completo"
              iconKey="user"
              error={errors?.name}
            />

            <InputField
              label="Nombre Comercial"
              name="tradeName"
              value={form.tradeName}
              required
              onChange={(e) => updateField('tradeName', e.target.value)}
              type="text"
              placeholder="Ingrese el nombre comercial"
              iconKey="user"
              error={errors?.tradeName}
            />

            {/* 👇 Solo uno de estos se muestra según el tipo seleccionado */}
            {selectedCode === '36' && (
              <InputField
                label="NIT"
                name="nit"
                value={form.nit}
                required
                onChange={(e) => updateField('nit', e.target.value)}
                type="text"
                placeholder="0000-000000-000-0"
                iconKey="idCard"
                error={errors?.nit}
              />
            )}

            {selectedCode === '13' && (
              <InputField
                label="DUI"
                name="dui"
                value={form.dui}
                required
                onChange={(e) => updateField('dui', e.target.value)}
                type="text"
                placeholder="00000000-0"
                iconKey="idCard"
                error={errors?.dui}
              />
            )}

            {selectedCode === '03' && (
              <InputField
                label="Pasaporte"
                name="passportNumber"
                value={form.passportNumber}
                required
                onChange={(e) => updateField('passportNumber', e.target.value)}
                type="text"
                iconKey="idCard"
                error={errors?.passportNumber}
              />
            )}

            {selectedCode === '02' && (
              <InputField
                label="Carnet de Residente"
                name="residentCardNumber"
                value={form.residentCardNumber}
                required
                onChange={(e) =>
                  updateField('residentCardNumber', e.target.value)
                }
                type="text"
                iconKey="idCard"
                error={errors?.residentCardNumber}
              />
            )}

            {selectedCode === '37' && (
              <InputField
                label="Otra Identificación"
                name="otherIdentification"
                value={form.otherIdentification}
                required
                onChange={(e) =>
                  updateField('otherIdentification', e.target.value)
                }
                iconKey="edit"
                type="text"
                error={errors?.otherIdentification}
              />
            )}

            {/* 👇 NRC siempre visible, no depende del tipo */}
            <InputField
              label="NRC"
              name="nrc"
              value={form.nrc}
              required
              onChange={(e) => updateField('nrc', e.target.value)}
              type="text"
              placeholder="Número de registro (NRC)"
              iconKey="idCard"
              error={errors?.nrc}
            />

            {/* Teléfono siempre visible */}
            <InputField
              label="Teléfono"
              name="phone"
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              type="text"
              placeholder="Ejemplo: 7777-7777"
              iconKey="phone"
              error={errors?.phone}
            />

            {/* Correo siempre visible */}
            <InputField
              label="Correo Electrónico"
              name="email"
              type="email"
              value={form.email}
              required
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="usuario@correo.com"
              iconKey="email"
              error={errors?.email}
            />
          </>
        );
      })()}

      <div className="md:col-span-3">
        {/* Encabezado de la sección */}
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            Tipo de Contribuyente
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona la categoría fiscal del cliente según los lineamientos
            establecidos por el Ministerio de Hacienda. Esta categoría
            determinará cómo se registran y procesan sus obligaciones
            tributarias en el sistema.
          </p>
        </div>

        {/* Campo del select con ancho controlado */}
        <div className="max-w-md">
          <SelectField
            label="Categoría del Contribuyente"
            name="taxpayerCategoryId"
            value={form.taxpayerCategoryId}
            required
            onChange={(e) =>
              updateField('taxpayerCategoryId', Number(e.target.value))
            }
            options={taxpayerCategoryDropdown.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            iconKey="list"
            error={errors?.taxpayerCategoryId}
          />
        </div>
      </div>

      {/* Actividad económica también ocupará las 3 columnas */}
      <div className="mb-6 border-b pb-4 md:col-span-3">
        <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
          Selecciona la Actividad Económica del Cliente
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
          Selecciona la actividad económica principal del cliente, según el
          catálogo oficial del Ministerio de Hacienda. Esto permitirá al sistema
          clasificar correctamente las operaciones del cliente.
        </p>
        {/* ✅ Acción rápida (crear + recargar) */}
        <QuickAddEconomicActivity
          onActivityCreated={onEconomicActivityCreated}
          onReload={onReloadEconomicActivities}
        />

        {/* 🧾 Selector */}
        <div className="mt-2 flex items-end gap-3">
          <div className="flex-1">
            <SimpleSearchSelect
              label="Actividad Económica"
              name="economicActivityId"
              options={economicActivitiesDropdown}
              selectedValue={form.economicActivityId}
              required
              onSelect={(option) =>
                updateField('economicActivityId', option.id)
              }
              iconKey="listOrdered"
              error={errors?.economicActivityId}
            />
          </div>
        </div>
      </div>

      {/* 🌍 Sección: País */}
      <div className="mb-6 border-b pb-4 md:col-span-3">
        <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
          País
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
          Selecciona el país de residencia del cliente (opcional).
        </p>

        {/* 🧾 Selector */}
        <div className="mt-2 flex items-end gap-3">
          <div className="max-w-md flex-1">
            <SimpleSearchSelect
              label="País"
              name="countryId"
              options={countryDropdown}
              selectedValue={form.countryId}
              onSelect={(option) =>
                updateField('countryId', option?.id ?? null)
              }
              iconKey="globe"
            />
          </div>
        </div>
      </div>

      {/* 📌 Sección: Dirección */}
      <div className="mt-5 md:col-span-3">
        {/* Encabezado de la sección */}
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            Dirección
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona el departamento y municipio del cliente y agrega la
            dirección completa (incluyendo distrito si aplica, según
            lineamientos del Ministerio de Hacienda).
          </p>
        </div>

        {/* Campos del selector */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SelectField
            label="Departamento"
            name="departmentId"
            value={form.departmentId}
            onChange={handleDepartmentChange}
            required
            options={departments.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            iconKey="list"
            error={errors?.departmentId}
          />

          <SelectField
            label="Municipio"
            name="municipalityId"
            value={form.municipalityId}
            required
            onChange={(e) =>
              updateField('municipalityId', Number(e.target.value))
            }
            options={municipalities.map((m) => ({
              value: m.id,
              label: m.name,
            }))}
            iconKey="list"
            error={errors?.municipalityId}
          />
        </div>

        {/* Complemento de la dirección */}
        <div className="mt-6">
          <TextAreaField
            label="Complemento"
            name="complement"
            value={form.complement}
            required
            onChange={(e) => updateField('complement', e.target.value)}
            iconKey="address"
            error={errors?.complement}
          />
        </div>
      </div>

      {/* Botones ocupan las 3 columnas */}
      {(onCancel || onSubmit) && (
        <div className="flex gap-4 md:col-span-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded bg-[#DC3545] px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
            >
              Cancelar
            </button>
          )}
          {onSubmit && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onSubmit}
              className={clsx(
                'flex-1 rounded px-4.5 py-2.5 font-medium text-white',
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-opacity-90'
              )}
            >
              {isSubmitting
                ? form.id
                  ? 'Actualizando...'
                  : 'Guardando...'
                : form.id
                ? 'Actualizar Cliente'
                : 'Registrar Cliente'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerNaturalForm;
